import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { defaultProducts, defaultVerificationTypes } from '../../data/mockData';
import { fetchClients, createClient, updateClient, fetchProducts } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Building2,
  Mail,
  Phone,
  Package,
  FileText,
  Upload,
  BarChart3,
  Edit,
  Eye,
  Trash2,
} from 'lucide-react';

export default function ClientManagement() {
  const { currentRole } = useApp();
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [showEditClientDialog, setShowEditClientDialog] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  
  useEffect(() => {
    let isMounted = true;
    Promise.all([
      fetchClients().catch(() => []),
      fetchProducts().catch(() => [])
    ]).then(([clRes, prRes]) => {
      if (!isMounted) return;
      setClients(Array.isArray(clRes) ? clRes : []);
      setProducts(Array.isArray(prRes) ? prRes : []);
    });
    return () => { isMounted = false; };
  }, []);
  
  const [newClientForm, setNewClientForm] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    logo: ''
  });
  
  const [editClientForm, setEditClientForm] = useState({
    id: '',
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    status: 'active',
    logo: ''
  });

  const [clientProductMappings, setClientProductMappings] = useState({});
  const [clientVerificationMappings, setClientVerificationMappings] = useState({});
  const [activeTabProductId, setActiveTabProductId] = useState(defaultProducts[0]?.id || '');

  const handleCreateClient = async () => {
    if (!newClientForm.name || !newClientForm.contactPerson || !newClientForm.email) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const payload = {
      name: newClientForm.name,
      contactPerson: newClientForm.contactPerson,
      phone: newClientForm.phone || '+91 99999 99999',
      email: newClientForm.email,
      logo: newClientForm.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100',
      status: 'active',
      products: ['Address Verification'],
      totalCases: 0
    };

    try {
      const created = await createClient(payload);
      const newEntry = (created && created.client) ? created.client : { ...payload, id: created?.id || `CLT${Date.now()}` };
      setClients(prev => [...prev, newEntry]);
      setNewClientForm({
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        logo: ''
      });
      setShowNewClientDialog(false);
      toast.success('Client created successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create client on server.');
    }
  };

  const handleOpenEditDialog = () => {
    setEditClientForm({
      id: selectedClient.id,
      name: selectedClient.name,
      contactPerson: selectedClient.contactPerson,
      phone: selectedClient.phone,
      email: selectedClient.email,
      status: selectedClient.status,
      logo: selectedClient.logo
    });
    setSelectedClient(null);
    setShowEditClientDialog(true);
  };

  const handleSaveEditClient = async () => {
    if (!editClientForm.name || !editClientForm.contactPerson || !editClientForm.email) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      await updateClient(editClientForm);
      setClients(prev => prev.map(c => c.id === editClientForm.id ? { ...c, ...editClientForm } : c));
      setShowEditClientDialog(false);
      toast.success('Client details updated successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update client.');
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    if (client && !clientProductMappings[client.id]) {
      const initialProducts = {};
      const initialVerifications = {};
      
      // Let's populate default products matching existing client.products strings
      defaultProducts.forEach(p => {
        const isMapped = client.products.some(cpName => cpName.toLowerCase() === p.name.toLowerCase());
        initialProducts[p.id] = isMapped;
        
        initialVerifications[p.id] = {};
        defaultVerificationTypes.forEach((vt, index) => {
          // pre-assign first 4 verification types for mapped products, otherwise false
          initialVerifications[p.id][vt.id] = isMapped && index < 4;
        });
      });
      
      setClientProductMappings(prev => ({ ...prev, [client.id]: initialProducts }));
      setClientVerificationMappings(prev => ({ ...prev, [client.id]: initialVerifications }));
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Client',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            <AvatarImage src={row.logo} />
            <AvatarFallback>{row.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-slate-900">{row.name}</p>
            <p className="text-xs text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'contactPerson',
      header: 'Contact Person',
      render: (row) => <span className="text-sm text-slate-700">{row.contactPerson}</span>,
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (row) => <span className="text-sm text-slate-500">{row.phone}</span>,
    },
    {
      key: 'products',
      header: 'Products',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.products.slice(0, 2).map((product, index) => (
            <Badge key={index} variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-700">
              {product}
            </Badge>
          ))}
          {row.products.length > 2 && (
            <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-700">
              +{row.products.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'totalCases',
      header: 'Total Cases',
      sortable: true,
      render: (row) => <span className="text-sm font-medium text-slate-900">{row.totalCases.toLocaleString()}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'success' : 'neutral'}
          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-management-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Client Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage client accounts and configurations</p>
        </div>
        {[ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.BUSINESS_PARTNER].includes(currentRole) && (
          <Button
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            onClick={() => setShowNewClientDialog(true)}
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{clients.length}</p>
              <p className="text-xs text-slate-500">Total Clients</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{clients.filter(c => c.status === 'active').length}</p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{products.length}</p>
              <p className="text-xs text-slate-500">Products Offered</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{clients.reduce((sum, c) => sum + (Number(c.totalCases) || 0), 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Cases</p>
            </div>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <DataTable
        columns={columns}
        data={clients}
        onRowClick={(row) => handleSelectClient(row)}
        searchPlaceholder="Search clients..."
        selectable={false}
        showActions={false}
      />

      {/* New Client Dialog */}
      <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Add New Client</DialogTitle>
            <DialogDescription className="text-slate-500">
              Create a new client account
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clientName">Client Name</Label>
              <Input 
                id="clientName" 
                className="bg-slate-50/50 border-slate-200 text-slate-800" 
                placeholder="Company name"
                value={newClientForm.name}
                onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contact Person</Label>
                <Input 
                  id="contactPerson" 
                  className="bg-slate-50/50 border-slate-200 text-slate-800" 
                  placeholder="Full name"
                  value={newClientForm.contactPerson}
                  onChange={(e) => setNewClientForm({ ...newClientForm, contactPerson: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input 
                  id="phone" 
                  className="bg-slate-50/50 border-slate-200 text-slate-800" 
                  placeholder="+91 99999 99999"
                  value={newClientForm.phone}
                  onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                className="bg-slate-50/50 border-slate-200 text-slate-800" 
                placeholder="client@company.com"
                value={newClientForm.email}
                onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewClientDialog(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateClient}>
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Client Profile</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="py-4">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="bg-slate-100/50 border border-slate-200 p-1">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                    Overview
                  </TabsTrigger>
                  {currentRole !== ROLES.BUSINESS_PARTNER && (
                    <>
                      <TabsTrigger value="products" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                        Products
                      </TabsTrigger>
                      <TabsTrigger value="rates" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                        Rates
                      </TabsTrigger>
                      <TabsTrigger value="reports" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                        Report Format
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>

                <TabsContent value="overview" className="mt-6 space-y-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={selectedClient.logo} />
                      <AvatarFallback>{selectedClient.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">{selectedClient.name}</h3>
                      <StatusBadge
                        status={selectedClient.status === 'active' ? 'success' : 'neutral'}
                        label={selectedClient.status}
                        size="sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Contact Person</p>
                      <p className="text-sm text-slate-900">{selectedClient.contactPerson}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm text-slate-900">{selectedClient.email}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Phone</p>
                      <p className="text-sm text-slate-900">{selectedClient.phone}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Total Cases</p>
                      <p className="text-sm text-slate-900 font-medium">{selectedClient.totalCases.toLocaleString()}</p>
                    </div>
                  </div>
                </TabsContent>

                {currentRole !== ROLES.BUSINESS_PARTNER && (
                  <TabsContent value="products" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 min-h-[400px]">
                      {/* Left Pane: Product Selection Grid */}
                      <div className="md:col-span-6 border-r border-slate-200/50 pr-4 space-y-4">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-200/40">
                          <span className="text-sm font-semibold text-slate-800">Products ({defaultProducts.length})</span>
                          <span className="text-xs text-slate-500">Toggle switch to activate</span>
                        </div>
                        
                        <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                          {defaultProducts.map(p => {
                            const mappings = clientProductMappings[selectedClient.id] || {};
                            const isActive = !!mappings[p.id];
                            const isFocused = activeTabProductId === p.id;
                            
                            return (
                              <div
                                key={p.id}
                                onClick={() => {
                                  if (isActive) {
                                    setActiveTabProductId(p.id);
                                  }
                                }}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                                  isFocused 
                                    ? 'bg-blue-50/50 border-blue-200 shadow-sm' 
                                    : 'bg-white border-slate-100 hover:border-slate-200'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                    isActive ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'
                                  }`}>
                                    <Package className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-800">{p.name}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{p.id}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isActive}
                                    disabled={![ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole)}
                                    onChange={(e) => {
                                      const val = e.target.checked;
                                      setClientProductMappings(prev => ({
                                        ...prev,
                                        [selectedClient.id]: {
                                          ...(prev[selectedClient.id] || {}),
                                          [p.id]: val
                                        }
                                      }));
                                      
                                      // If turning off product, clear verification mapping for it
                                      if (!val) {
                                        setClientVerificationMappings(prev => {
                                          const clientMap = { ...(prev[selectedClient.id] || {}) };
                                          delete clientMap[p.id];
                                          return { ...prev, [selectedClient.id]: clientMap };
                                        });
                                        if (activeTabProductId === p.id) {
                                          setActiveTabProductId('');
                                        }
                                      } else {
                                        // Auto focus it
                                        setActiveTabProductId(p.id);
                                        // Pre-fill default first 4 verifications as active
                                        setClientVerificationMappings(prev => {
                                          const clientMap = { ...(prev[selectedClient.id] || {}) };
                                          const verifs = {};
                                          defaultVerificationTypes.forEach((vt, index) => {
                                            verifs[vt.id] = index < 4;
                                          });
                                          clientMap[p.id] = verifs;
                                          return { ...prev, [selectedClient.id]: clientMap };
                                        });
                                      }
                                    }}
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
  
                      {/* Right Pane: Verification Type Checklist */}
                      <div className="md:col-span-6 space-y-4">
                        {(() => {
                          const mappings = clientProductMappings[selectedClient.id] || {};
                          const isCurrentActive = !!mappings[activeTabProductId];
                          const currentProduct = defaultProducts.find(p => p.id === activeTabProductId);
  
                          if (!isCurrentActive || !currentProduct) {
                            return (
                              <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed">
                                <Package className="w-12 h-12 text-slate-300 mb-3 animate-pulse" />
                                <p className="text-sm font-semibold text-slate-700">No Active Product Selected</p>
                                <p className="text-xs text-slate-500 mt-1 max-w-[200px]">
                                  Toggle on a product from the left list to map verification types.
                                </p>
                              </div>
                            );
                          }
  
                          return (
                            <>
                              <div className="pb-2 border-b border-slate-200/40">
                                <span className="text-sm font-semibold text-slate-800">
                                  Verification Types for: <span className="text-blue-600">{currentProduct.name}</span>
                                </span>
                              </div>
                              
                              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                                {defaultVerificationTypes.map(vt => {
                                  const vMap = clientVerificationMappings[selectedClient.id] || {};
                                  const pMap = vMap[activeTabProductId] || {};
                                  const isChecked = !!pMap[vt.id];
  
                                  return (
                                    <label
                                      key={vt.id}
                                      className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all cursor-pointer ${
                                        isChecked
                                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                                          : 'bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-600'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={![ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole)}
                                        onChange={(e) => {
                                          const val = e.target.checked;
                                          setClientVerificationMappings(prev => {
                                            const clientVMap = { ...(prev[selectedClient.id] || {}) };
                                            const productVMap = { ...(clientVMap[activeTabProductId] || {}) };
                                            productVMap[vt.id] = val;
                                            clientVMap[activeTabProductId] = productVMap;
                                            return { ...prev, [selectedClient.id]: clientVMap };
                                          });
                                        }}
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                                      />
                                      <span className="text-xs font-medium">{vt.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
  
                    {/* Save Assignment Button */}
                    {[ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole) && (
                      <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 mt-4">
                        <Button
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => {
                            toast.success(`Client product and verification configurations updated successfully for ${selectedClient.name}!`);
                          }}
                        >
                          Save Assignment Config
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                )}

                {currentRole !== ROLES.BUSINESS_PARTNER && (
                  <>
                    <TabsContent value="rates" className="mt-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4 text-xs font-medium text-slate-500 pb-2 border-b border-slate-200">
                          <span>Product</span>
                          <span>Client Rate</span>
                          <span>Vendor Payout</span>
                        </div>
                        {selectedClient.products.map((product, index) => (
                          <div key={index} className="grid grid-cols-3 gap-4 items-center py-2">
                            <span className="text-sm text-slate-900">{product}</span>
                            <span className="text-sm text-slate-900">₹{(250 + index * 50).toLocaleString()}</span>
                            <span className="text-sm text-slate-900">₹{(150 + index * 30).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </TabsContent>

                    <TabsContent value="reports" className="mt-6">
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-slate-50 border border-dashed border-slate-200 text-center">
                          <FileText className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                          <p className="text-sm text-slate-500 mb-3">Upload custom report format</p>
                          <Button 
                            variant="outline" 
                            className="bg-white border-slate-200 gap-2"
                            disabled={![ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole)}
                          >
                            <Upload className="w-4 h-4" />
                            Upload Template
                          </Button>
                        </div>
                        <div className="text-xs text-slate-500">
                          Supported formats: PDF, DOCX, XLSX
                        </div>
                      </div>
                    </TabsContent>
                  </>
                )}
              </Tabs>
            </div>
          )}
          {[ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole) && (
            <DialogFooter>
              <Button 
                variant="outline" 
                className="border-slate-200 text-slate-700 hover:bg-slate-50 gap-2"
                onClick={handleOpenEditDialog}
              >
                <Edit className="w-4 h-4" />
                Edit Client
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClientDialog} onOpenChange={setShowEditClientDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg text-slate-850">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Client Details</DialogTitle>
            <DialogDescription className="text-slate-500">
              Modify details for {editClientForm.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editClientName">Client Name</Label>
              <Input
                id="editClientName"
                className="bg-slate-50/50 border-slate-200 text-slate-800"
                value={editClientForm.name}
                onChange={(e) => setEditClientForm({ ...editClientForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editContactPerson">Contact Person</Label>
                <Input
                  id="editContactPerson"
                  className="bg-slate-50/50 border-slate-200 text-slate-800"
                  value={editClientForm.contactPerson}
                  onChange={(e) => setEditClientForm({ ...editClientForm, contactPerson: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editPhone">Phone</Label>
                <Input
                  id="editPhone"
                  className="bg-slate-50/50 border-slate-200 text-slate-800"
                  value={editClientForm.phone}
                  onChange={(e) => setEditClientForm({ ...editClientForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                className="bg-slate-50/50 border-slate-200 text-slate-800"
                value={editClientForm.email}
                onChange={(e) => setEditClientForm({ ...editClientForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editStatus">Status</Label>
              <select
                id="editStatus"
                className="w-full rounded-lg border border-slate-200 bg-slate-50/50 p-2.5 text-xs font-semibold text-slate-700 outline-none cursor-pointer"
                value={editClientForm.status}
                onChange={(e) => setEditClientForm({ ...editClientForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditClientDialog(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveEditClient}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
